import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Button } from './Button';
import { ApiError } from '@/lib/api/client';

/**
 * The four states every data view must handle (per the brief: loading,
 * skeletons, errors with retry, empty states). Centralised so no page
 * reinvents them and none is forgotten.
 */

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded bg-ink-100', className)} aria-hidden="true" />;
}

/** Placeholder matching a table's shape, so loading doesn't shift layout. */
export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="p-4" role="status" aria-label="Loading">
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex gap-4">
            {Array.from({ length: cols }).map((__, c) => (
              <Skeleton key={c} className={cn('h-4', c === 0 ? 'w-1/4' : 'flex-1')} />
            ))}
          </div>
        ))}
      </div>
      <span className="sr-only">Loading…</span>
    </div>
  );
}

export function CardSkeleton() {
  return (
    <div className="card p-4" role="status" aria-label="Loading">
      <Skeleton className="h-3 w-24" />
      <Skeleton className="mt-3 h-7 w-16" />
      <Skeleton className="mt-3 h-2 w-full" />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

interface EmptyStateProps {
  title: string;
  message?: string;
  icon?: ReactNode;
  action?: ReactNode;
}

export function EmptyState({ title, message, icon, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      {icon && <div className="mb-3 text-ink-300">{icon}</div>}
      <p className="font-display text-sm font-bold text-ink-800">{title}</p>
      {message && <p className="mt-1 max-w-sm text-xs text-ink-500">{message}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
}

/**
 * Error view with retry. Translates the server's error contract into language a
 * user can act on — a raw "Request failed (403)" tells them nothing.
 */
export function ErrorState({ error, onRetry }: ErrorStateProps) {
  const { title, message, retryable } = describeError(error);

  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-red2-100 text-red2-600">
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        </svg>
      </div>
      <p className="font-display text-sm font-bold text-ink-800">{title}</p>
      <p className="mt-1 max-w-sm whitespace-pre-line text-xs text-ink-500">{message}</p>
      {retryable && onRetry && (
        <Button variant="secondary" size="sm" className="mt-4" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

/** "body.items.0.unitPrice" → "Items #1 unit price"; "dueDate" → "Due date". */
function humanizeField(path: string): string {
  const words = path
    .replace(/^(body|query|params)\./, '')
    .split('.')
    .map((seg) => (/^\d+$/.test(seg) ? `#${Number(seg) + 1}` : seg.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase()))
    .join(' ')
    .replace(/\bid\b/g, 'ID');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Rewrite zod's terse defaults into plain sentences (already-custom messages pass through). */
function humanizeIssue(field: string, message: string): string {
  const label = humanizeField(field);
  const m = message || 'Invalid value';
  if (/^[A-Z]/.test(m) && !/^(Required|Invalid|Expected|String|Number)/.test(m)) return m; // already a custom sentence
  if (m === 'Required' || /received (null|undefined)/.test(m)) return `${label} is required`;
  if (m === 'Invalid uuid') return `${label} is not a valid selection`;
  if (m === 'Invalid email') return `${label} is not a valid email address`;
  if (m === 'Invalid date' || m === 'Invalid datetime') return `${label} is not a valid date`;
  if (m === 'Invalid url') return `${label} is not a valid link`;
  const minStr = m.match(/^String must contain at least (\d+)/);
  if (minStr) return Number(minStr[1]) <= 1 ? `${label} is required` : `${label} must be at least ${minStr[1]} characters`;
  const maxStr = m.match(/^String must contain at most (\d+)/);
  if (maxStr) return `${label} must be at most ${maxStr[1]} characters`;
  const num = m.match(/^Number must be (.+)$/);
  if (num) return `${label} must be ${num[1]}`;
  const en = m.match(/^Invalid enum value/);
  if (en) return `${label} has an invalid value`;
  return `${label}: ${m}`;
}

interface FieldIssue {
  field?: string;
  message?: string;
}

export function describeError(error: unknown): {
  title: string;
  message: string;
  retryable: boolean;
} {
  if (error instanceof ApiError) {
    if (error.isValidation && Array.isArray(error.details) && error.details.length > 0) {
      const lines = (error.details as FieldIssue[])
        .map((d) => humanizeIssue(d.field ?? '', d.message ?? ''))
        .map((s) => (s.endsWith('.') ? s : `${s}.`));
      return { title: 'Please check the form', message: [...new Set(lines)].join('\n'), retryable: false };
    }
    if (error.isForbidden) {
      return {
        title: 'You don’t have access',
        message: 'Your role doesn’t include permission to view this. Ask an administrator if you need it.',
        retryable: false,
      };
    }
    if (error.isPlanLimit) {
      return { title: 'Plan limit reached', message: error.message, retryable: false };
    }
    if (error.status === 404) {
      return { title: 'Not found', message: 'This record may have been deleted.', retryable: false };
    }
    if (error.status === 0) {
      return { title: 'Can’t reach the server', message: error.message, retryable: true };
    }
    return { title: 'Something went wrong', message: error.message, retryable: error.status >= 500 };
  }
  return {
    title: 'Something went wrong',
    message: error instanceof Error ? error.message : 'An unexpected error occurred.',
    retryable: true,
  };
}
